import { useCallback, useEffect, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import {
  AllTemplatesForBuilderDocument,
  GetVersionsDocument,
  TemplateTypeEnum,
  useAddAssessmentDetailsFieldsMutation,
  useAddMiServicesFieldsMutation,
  useAddMiServicesOverhaulFieldsForAssemblyTemplateMutation,
  useAddBspMiServicesOverhaulFieldsMutation,
  useAllTemplatesForBuilderQuery,
  useEvaluateAllAssembliesForStructureMutation,
  useSyncNestedFieldOptionsMutation,
} from '@/__generated__/graphql';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { useSnackBarMessage } from './utils';
import DownloadIcon from '@mui/icons-material/Download';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { AddTemplateFieldsToStructureDialog } from './AddTemplateFieldsToStructureDialog';
import { StructuresList } from './StructuresList';
import { TemplateForm } from './TemplateForm';
import {
  assemblyFields,
  assemblyTemplate,
  selectedTemplateField as selectedAssemblyField,
  selectedTemplateId,
} from './state';
import { GenericDialog } from '../shared/GenericDialog';
import { abyssColors } from '@/theme/colors';
import { AccountTree, Calculate } from '@mui/icons-material';
import { useAtom, useSetAtom } from 'jotai';
import { MiRecalculationDialog } from '../shared/MiRecalculationDialog';
import { PageLoader } from '../shared/PageLoader';

type AssemblyTemplateProps = {
  structureId: string;
  structureName: string;
};

export const AssemblyTemplate = ({ structureId, structureName }: AssemblyTemplateProps) => {
  const [allTemplateFields, setAllTemplateFields] = useAtom(assemblyFields);
  const setselectedTemplateField = useSetAtom(selectedAssemblyField);
  const [templateId, setTemplateId] = useAtom(selectedTemplateId);
  const setSelectedAssemblyTemplate = useSetAtom(assemblyTemplate);
  const [emptyForm, setEmptyForm] = useState<boolean>(false);
  const [showStructuresList, setShowStructuresList] = useState<boolean>(false);
  const [selectedStructureId, setSelectedStructureId] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isMiServicesConfirmationDialogOpen, setIsMiServicesConfirmationDialogOpen] =
    useState<boolean>(false);
  const [
    isMiServicesOverhaulConfirmationDialogOpen,
    setIsMiServicesOverhaulConfirmationDialogOpen,
  ] = useState<boolean>(false);
  const [isBackfillViewpointFieldMissingDialogOpen, setIsBackfillViewpointFieldMissingDialogOpen] =
    useState<boolean>(false);
  const [isBackfillViewpointConfirmDialogOpen, setIsBackfillViewpointConfirmDialogOpen] =
    useState<boolean>(false);
  const [isBackfillViewpointConfirmInProgress, setIsBackfillViewpointConfirmInProgress] =
    useState<boolean>(false);
  const [
    isBspMiServicesOverhaulConfirmationDialogOpen,
    setIsBspMiServicesOverhaulConfirmationDialogOpen,
  ] = useState<boolean>(false);
  const [isMIRecalucationDialogOpen, setIsMIRecalucationDialogOpen] = useState<boolean>(false);
  const [isNestedFieldOptionsSyncDialogOpen, setIsNestedFieldOptionsSyncDialogOpen] =
    useState<boolean>(false);
  const [
    isAssessmentDetailsFieldsConfirmationDialogOpen,
    setIsAssessmentDetailsFieldsConfirmationDialogOpen,
  ] = useState<boolean>(false);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const { data, refetch } = useAllTemplatesForBuilderQuery({
    variables: {
      structureId,
      type: TemplateTypeEnum.Assembly,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (data?.allTemplates && data.allTemplates.length > 0) {
      setSelectedAssemblyTemplate(data.allTemplates.at(0));
      setTemplateId(data.allTemplates.at(0)?.id);
      setAllTemplateFields(data.allTemplates.at(0)?.fields);
      setselectedTemplateField(data.allTemplates.at(0)?.fields.at(0));
    }
  }, [
    data?.allTemplates,
    setSelectedAssemblyTemplate,
    setAllTemplateFields,
    setTemplateId,
    setselectedTemplateField,
  ]);

  useEffect(() => {
    if (!isBackfillViewpointConfirmDialogOpen) {
      setIsBackfillViewpointConfirmInProgress(false);
    }
  }, [isBackfillViewpointConfirmDialogOpen]);

  const handlePlatformClick = useCallback(
    (id: string) => {
      if (id === structureId) {
        showSnackBar(`You have already selected ${structureName}`, 'info');
      } else {
        setSelectedStructureId(id);
        setIsOpen(true);
      }
    },
    [showSnackBar, structureId, structureName]
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const [addMiServicesFields] = useAddMiServicesFieldsMutation({
    refetchQueries: [AllTemplatesForBuilderDocument, GetVersionsDocument],
  });

  const handleAddMiServicesFields = useCallback(() => {
    addMiServicesFields({
      variables: {
        input: {
          structureId,
          type: TemplateTypeEnum.Assembly,
          templateId: templateId ?? '',
        },
      },
      onCompleted: (response) => {
        if (response?.addMiServicesFields) {
          showSnackBar('MI Services Fields added successfully', 'success');
          setIsMiServicesConfirmationDialogOpen(false);
        }
      },
      onError: (errorResponse) => {
        console.error(errorResponse);
        showSnackBar(`${errorResponse.message}`, 'error');
        setIsMiServicesConfirmationDialogOpen(false);
      },
    });
  }, [addMiServicesFields, showSnackBar, structureId, templateId]);

  const [evaluateAllAssemblies, { loading: updatingAssemblies }] =
    useEvaluateAllAssembliesForStructureMutation({
      refetchQueries: [AllTemplatesForBuilderDocument, GetVersionsDocument],
    });

  const handleEvaluation = useCallback(
    (evaluateManuallyAddedFields: boolean) => {
      evaluateAllAssemblies({
        variables: {
          structureId,
          evaluateManuallyAddedFields,
        },
        onCompleted: (response) => {
          if (response?.evaluateAllAssembliesForStructure) {
            showSnackBar('MI logic was successfully recalculated for this deployment', 'success');
            setIsMIRecalucationDialogOpen(false);
          }
        },
        onError: (errorResponse) => {
          console.error(errorResponse);
          showSnackBar(`${errorResponse.message}`, 'error');
          setIsMIRecalucationDialogOpen(false);
        },
      });
    },
    [evaluateAllAssemblies, showSnackBar, structureId]
  );

  const [addMiServicesOverhaulFields] = useAddMiServicesOverhaulFieldsForAssemblyTemplateMutation({
    refetchQueries: [AllTemplatesForBuilderDocument, GetVersionsDocument],
  });

  const handleAddMiServicesOverhaulFields = useCallback(() => {
    addMiServicesOverhaulFields({
      variables: {
        input: {
          structureId,
          type: TemplateTypeEnum.Assembly,
          templateId: templateId ?? '',
        },
      },
      onCompleted: (response) => {
        if (response?.addMiServicesOverhaulFields) {
          showSnackBar('MI Services Overhaul Fields added successfully', 'success');
          setIsMiServicesOverhaulConfirmationDialogOpen(false);
        }
      },
      onError: (errorResponse) => {
        console.error(errorResponse);
        showSnackBar(`${errorResponse.message}`, 'error');
        setIsMiServicesOverhaulConfirmationDialogOpen(false);
      },
    });
  }, [addMiServicesOverhaulFields, showSnackBar, structureId, templateId]);

  const ADD_VIEWPOINT_COUNT_FIELD = gql`
    mutation AddViewpointCountField($input: MiServicesInput) {
      addViewpointCountField(input: $input) {
        fields {
          id
          name
          key
        }
      }
    }
  `;
  const BACKFILL_VIEWPOINT_COUNTS = gql`
    mutation BackfillViewpointCounts($structureId: String!) {
      backfillViewpointCounts(structureId: $structureId)
    }
  `;
  const [addViewpointCountField, { loading: addingViewpointCountField }] = useMutation(
    ADD_VIEWPOINT_COUNT_FIELD,
    {
      refetchQueries: [AllTemplatesForBuilderDocument, GetVersionsDocument],
    }
  );
  const [backfillViewpointCounts, { loading: backfillViewpointCountsLoading }] =
    useMutation(BACKFILL_VIEWPOINT_COUNTS);

  const isBackfillConfirmDialogLoading =
    isBackfillViewpointConfirmInProgress || backfillViewpointCountsLoading;

  const isViewpointCountOperationLoading =
    addingViewpointCountField || backfillViewpointCountsLoading;

  const runBackfillViewpointCounts = useCallback(
    (successMessage?: string) => {
      setIsBackfillViewpointConfirmInProgress(true);
      // Defer mutation so loading state is painted first (no "job in background" message shown)
      const runMutation = () => {
        backfillViewpointCounts({
          variables: { structureId },
          onCompleted: (data: { backfillViewpointCounts?: boolean }) => {
            const message =
              successMessage ??
              (data?.backfillViewpointCounts
                ? 'Backfill started. Viewpoint counts will update in the background.'
                : 'Backfill could not be started.');
            showSnackBar(message, data?.backfillViewpointCounts ? 'success' : 'warning');
            setIsBackfillViewpointConfirmInProgress(false);
            setIsBackfillViewpointConfirmDialogOpen(false);
            setIsBackfillViewpointFieldMissingDialogOpen(false);
            refetch();
          },
          onError: (errorResponse: Error) => {
            console.error(errorResponse);
            showSnackBar(errorResponse.message, 'error');
            setIsBackfillViewpointConfirmInProgress(false);
            setIsBackfillViewpointConfirmDialogOpen(false);
            setIsBackfillViewpointFieldMissingDialogOpen(false);
          },
        });
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(runMutation);
      });
    },
    [backfillViewpointCounts, showSnackBar, structureId, refetch]
  );

  const handleAddViewpointCountFieldAndBackfill = useCallback(() => {
    addViewpointCountField({
      variables: {
        input: {
          structureId,
          type: TemplateTypeEnum.Assembly,
          templateId: templateId ?? '',
        },
      },
      onCompleted: () => {
        refetch()
          .then(() => runBackfillViewpointCounts('Viewpoints field added successfully.'))
          .catch((error) => {
            console.error(error);
            showSnackBar('Failed to refresh template data after adding field', 'error');
            setIsBackfillViewpointFieldMissingDialogOpen(false);
          });
      },
      onError: (errorResponse: Error) => {
        console.error(errorResponse);
        showSnackBar(errorResponse.message, 'error');
        setIsBackfillViewpointFieldMissingDialogOpen(false);
      },
    });
  }, [
    addViewpointCountField,
    refetch,
    runBackfillViewpointCounts,
    showSnackBar,
    structureId,
    templateId,
  ]);

  const handleBackfillViewpointCountClick = useCallback(() => {
    const hasViewpointsField = allTemplateFields?.some((f) => f.key === 'viewpoints');
    if (hasViewpointsField) {
      setIsBackfillViewpointConfirmDialogOpen(true);
    } else {
      setIsBackfillViewpointFieldMissingDialogOpen(true);
    }
  }, [allTemplateFields]);

  const [addBspMiServicesOverhaulFields] = useAddBspMiServicesOverhaulFieldsMutation({
    refetchQueries: [AllTemplatesForBuilderDocument, GetVersionsDocument],
  });

  const handleAddBspMiServicesOverhaulFields = useCallback(() => {
    addBspMiServicesOverhaulFields({
      variables: {
        input: {
          structureId,
          type: TemplateTypeEnum.Assembly,
          templateId: templateId ?? '',
        },
      },
      onCompleted: (response) => {
        if (response?.addBspMiServicesOverhaulFields) {
          showSnackBar('BSP MI Services Overhaul Fields added successfully', 'success');
          setIsBspMiServicesOverhaulConfirmationDialogOpen(false);
        }
      },
      onError: (errorResponse) => {
        console.error(errorResponse);
        showSnackBar(`${errorResponse.message}`, 'error');
        setIsBspMiServicesOverhaulConfirmationDialogOpen(false);
      },
    });
  }, [addBspMiServicesOverhaulFields, showSnackBar, structureId, templateId]);

  const [addAssessmentDetailsFields] = useAddAssessmentDetailsFieldsMutation({
    refetchQueries: [AllTemplatesForBuilderDocument, GetVersionsDocument],
  });

  const handleAddAssessmentDetailsFields = useCallback(() => {
    addAssessmentDetailsFields({
      variables: {
        input: {
          structureId,
          type: TemplateTypeEnum.Assembly,
          templateId: templateId ?? '',
        },
      },
      onCompleted: (response) => {
        if (response?.addAssessmentDetailsFields) {
          showSnackBar('Assessment Details Fields added successfully', 'success');
          setIsAssessmentDetailsFieldsConfirmationDialogOpen(false);
        }
      },
      onError: (errorResponse) => {
        console.error(errorResponse);
        showSnackBar(`${errorResponse.message}`, 'error');
        setIsAssessmentDetailsFieldsConfirmationDialogOpen(false);
      },
    });
  }, [addAssessmentDetailsFields, showSnackBar, structureId, templateId]);

  const [syncNestedFieldOptions, { loading: isSyncingNestedFieldOptions }] =
    useSyncNestedFieldOptionsMutation({
      refetchQueries: [AllTemplatesForBuilderDocument, GetVersionsDocument],
    });

  const handleSyncNestedFieldOptions = useCallback(async () => {
    if (!templateId) {
      showSnackBar('No template selected', 'error');
      return;
    }
    await syncNestedFieldOptions({
      variables: {
        templateId: templateId ?? '',
      },
      onCompleted: (response) => {
        if (response?.syncNestedFieldOptions) {
          showSnackBar('Sub-table fields synced. Filtering is now enabled.', 'success');
          setIsNestedFieldOptionsSyncDialogOpen(false);
        }
      },
      onError: (errorResponse) => {
        console.error(errorResponse);
        showSnackBar(
          `Failed to sync sub-table fields. Please contact support. ${errorResponse.message}`,
          'error'
        );
        setIsNestedFieldOptionsSyncDialogOpen(false);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncNestedFieldOptions, showSnackBar, structureId, templateId]);

  const downloadJson = useCallback(() => {
    refetch().then(({ data: allTemplatesData }) => {
      const allTemplates = allTemplatesData?.allTemplates;
      const jsonData = JSON.stringify(
        allTemplates?.map(({ __typename, ...template }) => template),
        undefined,
        2
      );
      const jsonBlob = new Blob([jsonData], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(jsonBlob);
      const fileName = `${structureName}-assembly-templates-${Date.now().toString()}.json`;
      link.download = fileName;
      document.body.append(link);
      link.click();
      link.remove();
    });
  }, [refetch, structureName]);

  return (
    <Stack>
      <Stack
        direction="row"
        gap={2}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          pt: 4,
          alignItems: 'center',
        }}
      >
        {showStructuresList && (
          <Typography variant="h6">Select a platform to copy fields from</Typography>
        )}

        <Grid container spacing={1} mx={2} justifyContent="flex-end">
          {showStructuresList && (
            <Grid size="auto">
              <Button
                startIcon={<VisibilityIcon />}
                variant="contained"
                onClick={() => {
                  setShowStructuresList(false);
                }}
                sx={containedButtonStyle}
              >
                View Template Fields
              </Button>
            </Grid>
          )}
          <Grid size="auto">
            <Button
              startIcon={<Calculate />}
              variant="contained"
              sx={containedButtonStyle}
              onClick={() => setIsMIRecalucationDialogOpen(true)}
            >
              MI Recalculation
            </Button>
          </Grid>
          <Grid size="auto">
            <Button
              startIcon={<AccountTree />}
              variant="contained"
              sx={containedButtonStyle}
              onClick={() => setIsNestedFieldOptionsSyncDialogOpen(true)}
            >
              Sync Sub-table Fields
            </Button>
          </Grid>
          <Grid size="auto">
            <Button
              startIcon={<AddCircleIcon />}
              variant="contained"
              sx={containedButtonStyle}
              onClick={() => setIsMiServicesConfirmationDialogOpen(true)}
            >
              Add MI Services Fields
            </Button>
          </Grid>
          <Grid size="auto">
            <Button
              startIcon={<AddCircleIcon />}
              variant="contained"
              sx={containedButtonStyle}
              onClick={() => setIsMiServicesOverhaulConfirmationDialogOpen(true)}
            >
              Add MI Services Overhaul Fields
            </Button>
          </Grid>
          <Grid size="auto">
            <Button
              startIcon={<AddCircleIcon />}
              variant="contained"
              sx={containedButtonStyle}
              onClick={handleBackfillViewpointCountClick}
            >
              Backfill Viewpoint Count
            </Button>
          </Grid>
          <Grid size="auto">
            <Button
              startIcon={<AddCircleIcon />}
              variant="contained"
              sx={containedButtonStyle}
              onClick={() => setIsBspMiServicesOverhaulConfirmationDialogOpen(true)}
            >
              Add BSP MI Services Overhaul Fields
            </Button>
          </Grid>
          <Grid size="auto">
            <Button
              startIcon={<AddCircleIcon />}
              variant="contained"
              sx={containedButtonStyle}
              onClick={() => setIsAssessmentDetailsFieldsConfirmationDialogOpen(true)}
            >
              Add Assessment Details Fields
            </Button>
          </Grid>
          <Grid size="auto">
            <Button
              startIcon={<FileCopyIcon />}
              variant="contained"
              sx={containedButtonStyle}
              onClick={() => {
                setShowStructuresList(true);
              }}
            >
              Copy Fields From Another Platform
            </Button>
          </Grid>
          {!showStructuresList && (
            <Grid size="auto">
              <Button
                startIcon={<DownloadIcon />}
                variant="contained"
                onClick={() => downloadJson()}
                sx={containedButtonStyle}
              >
                Download JSON
              </Button>
            </Grid>
          )}
        </Grid>
      </Stack>

      {!showStructuresList && (
        <TemplateForm
          handleEmptyForm={(value) => setEmptyForm(value)}
          emptyForm={emptyForm}
          currentTemplateType={TemplateTypeEnum.Assembly.toString()}
          structureId={structureId}
        />
      )}
      {showStructuresList && (
        <Box>
          <StructuresList onPlatformClick={handlePlatformClick} drawerOpen />
        </Box>
      )}
      {isOpen && (
        <AddTemplateFieldsToStructureDialog
          isOpen={isOpen}
          structureId={selectedStructureId}
          onClose={handleClose}
          structureName={structureName}
          currentStructureId={structureId}
          existingTemplateFields={allTemplateFields}
        />
      )}
      {isMiServicesConfirmationDialogOpen && (
        <GenericDialog
          open={isMiServicesConfirmationDialogOpen}
          onClose={() => setIsMiServicesConfirmationDialogOpen(false)}
          title="Add MI Services Fields"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to add MI Services Fields to this deployment?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setIsMiServicesConfirmationDialogOpen(false)}
                sx={textButtonStyle}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleAddMiServicesFields()}
                sx={containedButtonStyle}
              >
                Confirm
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}

      {isMiServicesOverhaulConfirmationDialogOpen && (
        <GenericDialog
          open={isMiServicesOverhaulConfirmationDialogOpen}
          onClose={() => setIsMiServicesOverhaulConfirmationDialogOpen(false)}
          title="Add MI Services Overhaul Fields"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to add MI Services Overhaul Fields to this deployment?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setIsMiServicesOverhaulConfirmationDialogOpen(false)}
                sx={textButtonStyle}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleAddMiServicesOverhaulFields()}
                sx={containedButtonStyle}
              >
                Confirm
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}

      {isBackfillViewpointFieldMissingDialogOpen && (
        <GenericDialog
          open={isBackfillViewpointFieldMissingDialogOpen}
          onClose={() =>
            !isViewpointCountOperationLoading && setIsBackfillViewpointFieldMissingDialogOpen(false)
          }
          title="Viewpoints field not in template"
          content={
            <>
              <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
                {isViewpointCountOperationLoading
                  ? 'Adding the Viewpoints field and starting backfill. This may take some time.'
                  : 'The Viewpoints field is not in this template. Add it and run backfill?'}
              </Typography>
              {isViewpointCountOperationLoading && (
                <Stack marginTop="3.2rem">
                  <PageLoader variant="sm" flashing hideProgress />
                </Stack>
              )}
            </>
          }
          actions={
            <>
              {!isViewpointCountOperationLoading && (
                <>
                  <Button
                    variant="text"
                    onClick={() => setIsBackfillViewpointFieldMissingDialogOpen(false)}
                    sx={textButtonStyle}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handleAddViewpointCountFieldAndBackfill()}
                    sx={containedButtonStyle}
                  >
                    Add & Backfill
                  </Button>
                </>
              )}
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}

      {isBackfillViewpointConfirmDialogOpen && (
        <GenericDialog
          open={isBackfillViewpointConfirmDialogOpen}
          onClose={() =>
            !isBackfillConfirmDialogLoading && setIsBackfillViewpointConfirmDialogOpen(false)
          }
          title="Backfill viewpoint count"
          content={
            <>
              <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
                {isBackfillConfirmDialogLoading
                  ? 'Backfill is being started. This may take a moment.'
                  : 'Backfill viewpoint count for all assemblies in this structure.'}
              </Typography>
              {isBackfillConfirmDialogLoading && (
                <Stack marginTop="3.2rem">
                  <PageLoader variant="sm" flashing hideProgress />
                </Stack>
              )}
            </>
          }
          actions={
            <>
              {!isBackfillConfirmDialogLoading && (
                <>
                  <Button
                    variant="text"
                    onClick={() => setIsBackfillViewpointConfirmDialogOpen(false)}
                    sx={textButtonStyle}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => runBackfillViewpointCounts()}
                    sx={containedButtonStyle}
                  >
                    Confirm
                  </Button>
                </>
              )}
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}

      {isBspMiServicesOverhaulConfirmationDialogOpen && (
        <GenericDialog
          open={isBspMiServicesOverhaulConfirmationDialogOpen}
          onClose={() => setIsBspMiServicesOverhaulConfirmationDialogOpen(false)}
          title="Add BSP MI Services Overhaul Fields"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to add BSP MI Services Overhaul Fields to this deployment?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setIsBspMiServicesOverhaulConfirmationDialogOpen(false)}
                sx={textButtonStyle}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleAddBspMiServicesOverhaulFields()}
                sx={containedButtonStyle}
              >
                Confirm
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
      {isNestedFieldOptionsSyncDialogOpen && (
        <GenericDialog
          open={isNestedFieldOptionsSyncDialogOpen}
          onClose={() => setIsNestedFieldOptionsSyncDialogOpen(false)}
          title="Sync Sub-table Fields"
          content={
            <Stack>
              <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
                {!isSyncingNestedFieldOptions ? (
                  <>
                    This will update existing fields and add new ones from sub-tables to the top
                    level, enabling filtering on sub-table data. No existing data will be changed or
                    removed.
                  </>
                ) : (
                  <>Please wait while sub-table fields are being synced.</>
                )}
              </Typography>
              {isSyncingNestedFieldOptions && (
                <Stack marginTop="3.2rem">
                  <PageLoader variant="sm" flashing hideProgress />
                </Stack>
              )}
            </Stack>
          }
          actions={
            !isSyncingNestedFieldOptions && (
              <>
                <Button
                  variant="text"
                  onClick={() => setIsNestedFieldOptionsSyncDialogOpen(false)}
                  sx={textButtonStyle}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleSyncNestedFieldOptions()}
                  sx={containedButtonStyle}
                >
                  Sync Fields
                </Button>
              </>
            )
          }
          actionsStyles={{ mr: 2, pb: 2 }}
          titleStyles={{ paddingBottom: '1.6rem', typography: 'h4' }}
        />
      )}

      <MiRecalculationDialog
        open={isMIRecalucationDialogOpen}
        onClose={() => setIsMIRecalucationDialogOpen(false)}
        onConfirm={handleEvaluation}
        loading={updatingAssemblies}
        entityType="assemblies"
      />

      {isAssessmentDetailsFieldsConfirmationDialogOpen && (
        <GenericDialog
          open={isAssessmentDetailsFieldsConfirmationDialogOpen}
          onClose={() => setIsAssessmentDetailsFieldsConfirmationDialogOpen(false)}
          title="Add Assessment Details Fields"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to add assessment details fields to this deployment?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setIsAssessmentDetailsFieldsConfirmationDialogOpen(false)}
                sx={textButtonStyle}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleAddAssessmentDetailsFields()}
                sx={containedButtonStyle}
              >
                Confirm
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
    </Stack>
  );
};
